import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { MenuPriceListApi } from "../../../../../../../../../../../../../../services/Api/Organization/types";
import { AssignPriceOptionType, ChangePriceByValueType } from "../../../../services/types";

interface Props {
	setOptionType: (option: AssignPriceOptionType) => void;
	optionType: AssignPriceOptionType;
	priceLists: MenuPriceListApi[];
	setSelectedPriceList: (priceList: MenuPriceListApi | null) => void;
	selectedPriceList: MenuPriceListApi | null;
	setNewPrice: (price: number | undefined) => void;
	newPrice?: number;
	setChangeByValueOption: React.Dispatch<React.SetStateAction<ChangePriceByValueType | undefined>>;
	changeByValueOption: ChangePriceByValueType | undefined;
}

const AssignPricesModalOptions = ({
	setOptionType,
	optionType,
	priceLists,
	setSelectedPriceList,
	selectedPriceList,
	newPrice,
	setNewPrice,
	setChangeByValueOption,
	changeByValueOption,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const helpTextStyles = { marginLeft: "36px" };
	const [innerNewPrice, setInnerNewPrice] = useState<string | undefined>(newPrice ? newPrice.toString() : undefined);

	const handleChangeOptionType = (option: AssignPriceOptionType) => {
		setNewPrice(undefined);

		if (
			option === AssignPriceOptionType.CHANGE_PRICE_LIST_PRICE_BY_VALUE ||
			option === AssignPriceOptionType.CHANGE_DEFAULT_PRICE_BY_VALUE
		) {
			setChangeByValueOption(ChangePriceByValueType.INCREASE);
		} else {
			setChangeByValueOption(undefined);
		}

		setOptionType(option);
	};

	const handleChangeByValueOption = (option: ChangePriceByValueType) => {
		setChangeByValueOption(option);
	};

	const handleOnChangeNumberInput = (value?: string) => {
		let valueWithDigitsOnly = (value || "").replace(/[^0-9.,]/g, "");
		valueWithDigitsOnly = valueWithDigitsOnly.replace(",", ".");

		const dotParts = valueWithDigitsOnly.split(".");
		if (dotParts.length > 2) {
			valueWithDigitsOnly = `${dotParts[0]}.${dotParts[1]}`;
		}

		if (dotParts.length === 2) {
			const beforeDecimal = dotParts[0];
			const afterDecimal = dotParts[1].slice(0, 2);
			valueWithDigitsOnly = `${beforeDecimal}.${afterDecimal}`;
		}

		setInnerNewPrice(valueWithDigitsOnly);
		const isValid = /^\d*\.?\d{0,2}$/.test(valueWithDigitsOnly);

		if (isValid) {
			const numericValue = parseFloat(valueWithDigitsOnly);
			setNewPrice(!isNaN(numericValue) ? numericValue : undefined);
		} else {
			setNewPrice(undefined);
		}
	};

	return (
		<>
			<div className="d-flex flex-column mb-3">
				<Form.Check
					type="switch"
					id={AssignPriceOptionType.DEFAULT_PRICE}
					label={t("enums.price_lists.assign_price_options.DEFAULT_PRICE.title")}
					checked={optionType === AssignPriceOptionType.DEFAULT_PRICE}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.DEFAULT_PRICE)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.DEFAULT_PRICE.help_text")}
				</small>
			</div>
			<div className="d-flex flex-column mb-3">
				<Form.Check
					type="switch"
					id={AssignPriceOptionType.PRICE_BASED_ON_PRICE_LISTS}
					label={t("enums.price_lists.assign_price_options.PRICE_BASED_ON_PRICE_LISTS.title")}
					checked={optionType === AssignPriceOptionType.PRICE_BASED_ON_PRICE_LISTS}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.PRICE_BASED_ON_PRICE_LISTS)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.PRICE_BASED_ON_PRICE_LISTS.help_text")}
				</small>
				{optionType === AssignPriceOptionType.PRICE_BASED_ON_PRICE_LISTS && (
					<Select
						className="price-list-select mt-2"
						value={selectedPriceList}
						onChange={(value) => setSelectedPriceList(value)}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id.toString()}
						options={priceLists}
						placeholder={t("lib:common.action.select")}
					/>
				)}
			</div>
			<div className="d-flex flex-column mb-3">
				<Form.Check
					id={AssignPriceOptionType.SET_PRICE}
					type="switch"
					label={t("enums.price_lists.assign_price_options.SET_PRICE.title")}
					checked={optionType === AssignPriceOptionType.SET_PRICE}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.SET_PRICE)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.SET_PRICE.help_text")}
				</small>
				{optionType === AssignPriceOptionType.SET_PRICE && (
					<input
						className="form-control mt-2"
						value={innerNewPrice}
						placeholder={t("modules.group_edit.field.new_price.placeholder")}
						onChange={(e) => handleOnChangeNumberInput(e.target.value)}
					/>
				)}
			</div>
			<div className="d-flex flex-column mb-3">
				<Form.Check
					id={AssignPriceOptionType.CHANGE_DEFAULT_PRICE_BY_VALUE}
					type="switch"
					label={t("enums.price_lists.assign_price_options.CHANGE_DEFAULT_PRICE_BY_VALUE.title")}
					checked={optionType === AssignPriceOptionType.CHANGE_DEFAULT_PRICE_BY_VALUE}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.CHANGE_DEFAULT_PRICE_BY_VALUE)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.CHANGE_DEFAULT_PRICE_BY_VALUE.help_text")}
				</small>
				{optionType === AssignPriceOptionType.CHANGE_DEFAULT_PRICE_BY_VALUE && (
					<div className="ps-4">
						<div className="my-3">
							<Form.Check
								id={ChangePriceByValueType.INCREASE}
								type="radio"
								label={t("enums.price_lists.assign_price_options.INCREASE.title")}
								checked={changeByValueOption === ChangePriceByValueType.INCREASE}
								onChange={() => handleChangeByValueOption(ChangePriceByValueType.INCREASE)}
							/>
							<Form.Check
								id={ChangePriceByValueType.DECREASE}
								type="radio"
								label={t("enums.price_lists.assign_price_options.DECREASE.title")}
								checked={changeByValueOption === ChangePriceByValueType.DECREASE}
								onChange={() => handleChangeByValueOption(ChangePriceByValueType.DECREASE)}
							/>
						</div>
						<input
							className="form-control mt-2"
							value={innerNewPrice}
							placeholder={t("common.word.value")}
							onChange={(e) => handleOnChangeNumberInput(e.target.value)}
						/>
					</div>
				)}
			</div>
			<div className="d-flex flex-column mb-3">
				<Form.Check
					id={AssignPriceOptionType.CHANGE_PRICE_LIST_PRICE_BY_VALUE}
					type="switch"
					label={t("enums.price_lists.assign_price_options.CHANGE_PRICE_LIST_PRICE_BY_VALUE.title")}
					checked={optionType === AssignPriceOptionType.CHANGE_PRICE_LIST_PRICE_BY_VALUE}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.CHANGE_PRICE_LIST_PRICE_BY_VALUE)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.CHANGE_PRICE_LIST_PRICE_BY_VALUE.help_text")}
				</small>
				{optionType === AssignPriceOptionType.CHANGE_PRICE_LIST_PRICE_BY_VALUE && (
					<div className="ps-4">
						<div className="my-3">
							<Form.Check
								id={ChangePriceByValueType.INCREASE}
								type="radio"
								label={t("enums.price_lists.assign_price_options.INCREASE.title")}
								checked={changeByValueOption === ChangePriceByValueType.INCREASE}
								onChange={() => handleChangeByValueOption(ChangePriceByValueType.INCREASE)}
							/>
							<Form.Check
								id={ChangePriceByValueType.DECREASE}
								type="radio"
								label={t("enums.price_lists.assign_price_options.DECREASE.title")}
								checked={changeByValueOption === ChangePriceByValueType.DECREASE}
								onChange={() => handleChangeByValueOption(ChangePriceByValueType.DECREASE)}
							/>
						</div>
						<input
							className="form-control mt-2"
							value={innerNewPrice}
							placeholder={t("common.word.value")}
							onChange={(e) => handleOnChangeNumberInput(e.target.value)}
						/>
					</div>
				)}
			</div>
			<div className="d-flex flex-column">
				<Form.Check
					id={AssignPriceOptionType.RESET}
					type="switch"
					label={t("enums.price_lists.assign_price_options.RESET.title")}
					checked={optionType === AssignPriceOptionType.RESET}
					onChange={() => handleChangeOptionType(AssignPriceOptionType.RESET)}
				/>
				<small className="text-muted" style={helpTextStyles}>
					{t("enums.price_lists.assign_price_options.RESET.help_text")}
				</small>
			</div>
		</>
	);
};

export default AssignPricesModalOptions;
